import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, MessageSquare, FileText, Link as LinkIcon, Mail, Download } from 'lucide-react';
import { BRAND_COLORS, BRAND_LABELS, BACKEND_URL } from '@/constants/bluemonk';

export const ActionRequiredTab = ({ items, today, onSubmit }) => {
  const sorted = [...items].sort((a, b) => {
    const aOverdue = a.status === 'pending' && a.deadline < today;
    const bOverdue = b.status === 'pending' && b.deadline < today;
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;
    return new Date(a.deadline) - new Date(b.deadline);
  });

  if (sorted.length === 0) {
    return (
      <Card className="p-8 text-center">
        <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <p className="text-lg font-medium text-slate-700">No pending inputs</p>
        <p className="text-slate-500">All caught up!</p>
      </Card>
    );
  }

  const getSubmissionIcon = (type) => {
    switch (type) {
      case 'file': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'link': return <LinkIcon className="h-4 w-4 text-purple-500" />;
      case 'shared-via-email': return <Mail className="h-4 w-4 text-amber-500" />;
      default: return <MessageSquare className="h-4 w-4 text-green-500" />;
    }
  };

  const getSubmissionLabel = (type) => {
    switch (type) {
      case 'file': return 'Uploaded file';
      case 'link': return 'Shared link';
      case 'shared-via-email': return 'Sent via email';
      default: return 'Provided input';
    }
  };

  return (
    <div className="space-y-4">
      {sorted.map((item) => {
        const isOverdue = item.status === 'pending' && item.deadline < today;
        const isSubmitted = item.status === 'submitted' || item.status === 'accepted';
        const colors = BRAND_COLORS[item.brand];
        
        return (
          <Card 
            key={item.id} 
            className={`p-6 border-l-4 ${colors.border} ${isOverdue ? 'bg-red-50/50' : ''} ${isSubmitted ? 'bg-green-50/30' : ''}`}
            data-testid={`input-card-${item.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <Badge className={`${colors.badge} text-white text-xs`}>
                    {BRAND_LABELS[item.brand]}
                  </Badge>
                  <Badge variant={item.priority === 'P0' ? 'destructive' : 'secondary'}>
                    {item.priority}
                  </Badge>
                  {isOverdue && (
                    <Badge variant="destructive">Overdue</Badge>
                  )}
                  {isSubmitted ? (
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Provided
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="capitalize">
                      {item.status.replace('-', ' ')}
                    </Badge>
                  )}
                </div>
                
                <p className="text-sm text-slate-500 italic mb-3">{item.whyNeeded}</p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">Deadline:</span>{' '}
                    <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                      {new Date(item.deadline).toLocaleDateString('en-US', { 
                        month: 'short', day: 'numeric', year: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Format:</span>{' '}
                    {item.formatRequired}
                  </div>
                </div>
                
                {item.blocksDeliverables?.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-slate-700">Blocks:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.blocksDeliverables.map((d, i) => (
                        <Badge key={d + i} variant="outline" className="text-xs">
                          {d}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submissions Section - Show ALL submissions with full details */}
                {item.submissions?.length > 0 && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm font-semibold text-green-800 mb-3">
                      <CheckCircle2 className="h-4 w-4 inline mr-1" />
                      Inputs Provided ({item.submissions.length})
                    </p>
                    <div className="space-y-3">
                      {item.submissions.map((sub) => (
                        <div key={sub.id} className="p-3 bg-white rounded-lg border border-green-100">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              {getSubmissionIcon(sub.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-slate-800">{sub.submittedBy?.name}</span>
                                <span className="text-xs text-slate-500">
                                  {getSubmissionLabel(sub.type)}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {new Date(sub.submittedAt).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              
                              {/* Show text content */}
                              {sub.textContent && (
                                <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-slate-700 whitespace-pre-wrap">
                                  {sub.textContent}
                                </div>
                              )}
                              
                              {/* Show note */}
                              {sub.note && sub.note !== sub.textContent && (
                                <p className="mt-2 text-sm text-slate-600 italic">
                                  Note: "{sub.note}"
                                </p>
                              )}
                              
                              {/* Show file download */}
                              {sub.type === 'file' && sub.fileUrl && (
                                <a
                                  href={`${BACKEND_URL}${sub.fileUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-sm hover:bg-blue-100 transition-colors"
                                >
                                  <Download className="h-4 w-4" />
                                  {sub.fileName || 'Download File'}
                                </a>
                              )}
                              
                              {/* Show link */}
                              {sub.type === 'link' && sub.linkUrl && (
                                <a
                                  href={sub.linkUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-md text-sm hover:bg-purple-100 transition-colors break-all"
                                >
                                  <LinkIcon className="h-4 w-4 flex-shrink-0" />
                                  {sub.linkUrl.length > 50 ? sub.linkUrl.substring(0, 50) + '...' : sub.linkUrl}
                                </a>
                              )}
                              
                              {/* Shared via email indicator */}
                              {sub.type === 'shared-via-email' && (
                                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-md text-sm">
                                  <Mail className="h-4 w-4" />
                                  Shared via email to Content Vista team
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                {item.comments?.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-semibold text-blue-800 mb-3">
                      <MessageSquare className="h-4 w-4 inline mr-1" />
                      Comments ({item.comments.length})
                    </p>
                    <div className="space-y-2">
                      {item.comments.map((comment) => (
                        <div key={comment.id} className="p-2 bg-white rounded border border-blue-100">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{comment.author?.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {comment.authorType === 'admin' ? 'CV Team' : 'Client'}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {new Date(comment.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 mt-1">{comment.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={() => onSubmit(item)}
                variant={isSubmitted ? "outline" : "default"}
                className={`ml-4 flex-shrink-0 ${isSubmitted ? "" : "bg-primary hover:bg-primary/90"}`}
                data-testid={`submit-btn-${item.id}`}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {isSubmitted ? 'Add More' : 'Provide Inputs'}
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
